import axios from 'axios'
import { captchaAxios } from 'api/config'
import { store } from 'store/index'
import { settingsSelectors } from 'store/settings'

// Parametri aggiunti dalla firma CloudFront.
const SIGNATURE_PARAMS = ['Policy', 'Signature', 'Key-Pair-Id', 'Expires']

/**
 * Toglie la firma da un URL, lasciando intatto il resto della query string.
 *
 * Il server risale all'upload cercando l'URL memorizzato, che è quello nudo: con una firma
 * attaccata (tipico di un link già firmato e scaduto) non troverebbe nulla.
 */
const stripSignature = (url: string): string => {
  try {
    const parsed = new URL(url)
    SIGNATURE_PARAMS.forEach((param) => parsed.searchParams.delete(param))
    return parsed.toString()
  } catch {
    return url
  }
}

/**
 * Scambia l'URL memorizzato di un upload con uno firmato, valido 2 ore.
 *
 * Gli upload sono privati e l'URL che il server persiste è quello CDN nudo, che da solo vale
 * 403. Le citazioni in particolare vivono dentro il testo del messaggio salvato: lì l'URL DEVE
 * restare nudo, perché una firma ci resterebbe congelata dentro e sarebbe morta dopo due ore.
 *
 * `captchaAxios` porta già il token altcha e il `tenantId` del portal nei params, che il
 * server usa per verificare che l'upload appartenga a questo tenant.
 *
 * Se l'URL non corrisponde a nessun upload (sorgenti `site`, link esterni) torna invariato,
 * così i chiamanti non devono distinguere i due casi.
 */
export const getSignedUrl = async (url?: string | null): Promise<string> => {
  if (!url) return ''

  // Logged in (user or external token) the server authenticates the bearer and never reads the
  // Altcha token: skip solving one (~100-170ms, ~1s on the first request). Guests still need it.
  const client = settingsSelectors.isLogged(store.getState()) ? axios : captchaAxios

  try {
    const { data } = await client.get<{ url: string }>('uploads/link', {
      params: { url: stripSignature(url) },
    })
    return data.url
  } catch {
    return url
  }
}

const escapeAttribute = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

/**
 * Apre un upload in una nuova scheda, firmandolo al click.
 *
 * ! La finestra si apre SUBITO e si naviga dopo: `window.open` chiamato dopo un `await` perde
 * ! la user activation del click e viene bloccato dal popup blocker.
 */
/**
 * Salva un upload come `fileName`, anche un PDF che il browser aprirebbe nel suo visualizzatore.
 *
 * `<a download>` su un url di un altro dominio viene ignorato, e la CDN non manda header CORS,
 * quindi un fetch dell'url firmato non puo leggere i byte. Li legge dal server (`uploads/download`,
 * stesse regole di `uploads/link`, ma in streaming) e li salva da un blob url: funziona anche
 * dentro un iframe, purche l'host non lo sandboxi senza `allow-downloads`.
 * Se il server non risponde si ripiega sull'apertura dell'url firmato in una nuova scheda; resolve
 * a false solo se non si e potuto fare nemmeno quello (popup bloccato).
 */
export const downloadSignedUrl = async (url: string | null | undefined, fileName: string): Promise<boolean> => {
  if (!url) return false

  const client = settingsSelectors.isLogged(store.getState()) ? axios : captchaAxios

  try {
    const { data } = await client.get<Blob>('uploads/download', {
      params: { url: stripSignature(url) },
      responseType: 'blob',
    })
    const blobUrl = URL.createObjectURL(data)
    const link = Object.assign(document.createElement('a'), { href: blobUrl, download: fileName })
    link.click()
    // il click avvia solo il download: si revoca quando il browser ha gia preso il blob
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    return true
  } catch (e) {
    console.error(e)
    // ! mai window.location qui: navigherebbe via il notebook stesso
    const target = window.open(await getSignedUrl(url), '_blank')
    if (target) target.opener = null
    return !!target
  }
}

export const openSignedUrl = async (url?: string | null, fragment = '') => {
  if (!url) return

  const target = window.open('', '_blank')

  if (target) {
    // niente accesso a window.opener dalla pagina aperta
    target.opener = null
  }

  const signed = await getSignedUrl(url)

  // il frammento va DOPO la query string della firma, o finisce dentro la signature
  const destination = `${signed}${fragment}`

  if (target) {
    // ! NON `target.location.href`: per un file che il browser non renderizza (xlsx) quella
    // ! navigazione è un download, e Chromium lo blocca quando a iniziarlo è un altro frame (noi,
    // ! dentro l'iframe sandboxed dell'host) lasciando il popup su about:blank. Se invece il popup
    // ! si naviga da solo (meta refresh scritto nel suo documento) il download parte. Il PDF si
    // ! renderizza in entrambi i casi: stesso giro per tutti.
    target.document.write(`<!doctype html><meta http-equiv="refresh" content="0;url=${escapeAttribute(destination)}">`)
    target.document.close()
    return
  }

  // popup bloccato comunque: meglio navigare nella scheda corrente che non fare nulla
  window.location.href = destination
}
